import _instanceof from "@swc/helpers/src/_instanceof.mjs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
// timezone seems to depend on utc for some calls.
dayjs.extend(utc);
dayjs.extend(timezone);
// Type checks use duck-type checking, to support values from multiple sources (different polyfill, native).
var isObject = function(value) {
    return typeof value === "object" && value !== null;
};
// While not on the interface, use 'since' and 'until' to check for Instant and ZonedDateTime,
// so we don't match objects having only `epochMilliseconds`.
var isTemporalAbsolute = function(value) {
    return isObject(value) && "epochMilliseconds" in value && "since" in value && "until" in value;
};
var toDayjs = function(value) {
    if (isTemporalAbsolute(value)) {
        if ("timeZoneId" in value) {
            return dayjs.tz(value.epochMilliseconds, value.timeZoneId);
        }
        return dayjs(value.epochMilliseconds);
    }
    // Passing a Dayjs value through dayjs can have side-effects, so check.
    return dayjs.isDayjs(value) ? value : dayjs(value);
};
var isDayjsWithInternals = function(value) {
    return dayjs.isDayjs(value) && "$x" in value && isObject(value.$x);
};
var toDate = function(value) {
    if (_instanceof(value, Date)) {
        return value;
    }
    if (isTemporalAbsolute(value)) {
        return new Date(value.epochMilliseconds);
    }
    return dayjs(value).toDate();
};
var timeZoneFromValue = function(value) {
    if (isDayjsWithInternals(value)) {
        // Property absent means no timezone, property explicitly undefined means default timezone.
        if ("$timezone" in value.$x) {
            var _$timezone;
            return (_$timezone = value.$x.$timezone) !== null && _$timezone !== void 0 ? _$timezone : dayjs.tz.guess();
        }
    } else if (isTemporalAbsolute(value) && "timeZoneId" in value) {
        return value.timeZoneId;
    }
    return null;
};
export var TemporalUtils = Object.freeze({
    /**
     * Check whether `value` represents an absolute moment in time, e.g. one that can be translated to an epoch.
     *
     * @param value The value to check.
     * @returns Whether the value is either {@link InstantLike} or {@link ZonedDateTimeLike}.
     */ isTemporalAbsolute: isTemporalAbsolute,
    /**
     * Extract time zone information from the value, if it has any.
     *
     * @param value The value to inspect.
     */ timeZoneFromValue: timeZoneFromValue,
    /**
     * Normalize a value to a {@link Date}, potentially removing time zone information(!).
     *
     * @param value The value to normalize.
     * @returns The {@link Date} value.
     */ toDate: toDate,
    /**
     * Normalize a value to a {@link dayjs.Dayjs} value, preserving time zone information if applicable.
     *
     * @param value The value to normalize.
     * @returns The {@link dayjs.Dayjs} value.
     */ toDayjs: toDayjs
});

//# sourceMappingURL=TemporalUtils.js.map